/**
 * The extent of a set of GeoJSON features, for fitBounds after a municipality
 * loads.
 */

import type { Feature, Geometry, Position } from "geojson";

/**
 * Every coordinate pair in a polygonal geometry, flattened.
 *
 * Only Polygon and MultiPolygon: the buildings layer is the only caller. An
 * unhandled type here means the pipeline changed shape, and an empty extent
 * makes that visible immediately as a map that does not move.
 */
export function flatCoords(geometry: Geometry | null | undefined): Position[] {
  if (!geometry) return [];
  if (geometry.type === "Polygon") return geometry.coordinates.flat();
  if (geometry.type === "MultiPolygon") return geometry.coordinates.flat(2);
  return [];
}

export type Bounds = [[number, number], [number, number]];

/**
 * South-west and north-east corners, or null when nothing was measurable.
 *
 * The legacy version returned `[[Infinity, Infinity], [-Infinity, -Infinity]]`
 * for an empty collection and handed it straight to fitBounds, which throws.
 * Returning null makes the caller decide, and there is a real case for it: a
 * letter filter can exclude every building in a municipality.
 */
export function getBounds(features: readonly Feature[]): Bounds | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const feature of features) {
    for (const position of flatCoords(feature.geometry)) {
      const [lng, lat] = position;
      if (lng == null || lat == null) continue;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }

  if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return null;
  return [[minLng, minLat], [maxLng, maxLat]];
}
